import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { TransitionGroup } from 'react-transition-group';

import Browser from '@wetransfer/concorde-browser';

import Store from '@app/store';
import SplashpageHelper from '@app/helper/splashpage';
import Helper from '@app/helper';

import Router from '@module/router';

// Load the main components for our application
import Dropzone from '@component/dropzone';
import Footer from '@component/footer';
import Logo from '@component/logo';
import MobileNavigation from '@component/navigation/mobile';
import Navigation from '@component/navigation';
import NavigationConfig from '@component/navigation/config';
import NoticeBar from '@component/noticebar';
import Panel from '@component/panel';
import Splashpage from '@component/splashpage';
import Welcome, {
  STEP_TERMS_AND_CONDITIONS,
  STEP_COOKIES,
} from '@component/welcome';
import Sticky from '@component/sticky';
import Transfer from '@component/transfer';
import { isConsentActivated } from '@component/consent-manager';

import CookieBanner from '@component/cookie-banner';

// Keep track of our component states and route, this is the
// way we'll make sure multi-component routing works.
const componentStates = {};
const componentRoutes = {};

// Lazy loaded wallpaper component
const Wallpaper = lazy(() =>
  import(/* webpackChunkName: "wallpaper" */ '../../wallpaper-bundle')
);

class Layout extends React.Component {
  state = {
    showSplashpage: SplashpageHelper.shouldDisplaySplashpage(),
  };

  // When receiving props let's take a look at what needs to happen.
  static getDerivedStateFromProps(props, state) {
    if (!props.route) {
      return;
    }

    // Update the component state and route for this route type.
    const { type, updateTransfer } = props.route;

    // We keep the transfer id + secret when moving to advertise/wallpaper because it prevents the component from losing its state
    const advert_id = props.route.params.advert_id;
    if (advert_id) {
      props.route.params = { ...componentStates[type], advert_id };
    }

    componentStates[type] = props.route.params;
    componentRoutes[type] = props.route;

    // Explicitly update the state of the transfer for some panel routes.
    if (updateTransfer) {
      componentStates.transfer = props.route.params;
      componentRoutes.transfer = props.route;
    }

    return state;
  }

  closeSplashpage = () => {
    this.setState({ showSplashpage: false }, () => {
      SplashpageHelper.writeCookie();
    });
  };

  navigateToSignIn = (trackingCode, pathname, tier) => {
    this.setState({ showSplashpage: false });

    const target = {
      pathname: pathname || '/pro',
    };

    const search = new URLSearchParams();

    if (trackingCode) {
      search.set('trk', trackingCode);
    }

    if (tier) {
      search.set('tier', tier);
    }

    const searchString = search.toString();
    if (searchString !== '') {
      target.search = searchString;
    }

    setTimeout(() => {
      Router.navigateTo(target.pathname, false, target);
    }, 300);
  };

  renderWallpaperComponent() {
    return (
      <Suspense fallback={null}>
        <Wallpaper />
      </Suspense>
    );
  }

  // We'll draw our main components
  render() {
    const {
      session,
      transfer,
      transferUpload,
      user,
      consent,
    } = Store.getState();
    const className = 'app application';
    const transferProps = componentStates['transfer'] || {};
    const route = { ...this.props.route };
    const incompatibleVersion = session.incompatible_version;
    const transferPasswordNeeded =
      route.requiresTransferPassword &&
      (!transfer ||
        !transfer.state ||
        (!transfer.password && transfer.state === 'password_protected'));
    let dropzone = null;
    let iconComponent = null;
    let navComponent = null;
    let panelComponent = null;
    let wallpaperComponent = null;
    let transferComponent = null;
    let footerComponent = null;
    let welcome = null;
    let cookieBanner = null;

    const { text, color, onClose, showCloseIcon } = Helper.getNoticeOptions({
      incompatibleVersion,
      user,
    });
    const noticeOptions = {
      text,
      color,
      route,
      transferNode: this.transferNode,
      transferState: transfer && transfer.state,
      onClose,
      showCloseIcon,
    };
    const noticeBar = noticeOptions.text && <NoticeBar {...noticeOptions} />;

    // Don't show the panel if the route requires TOC but hasn't been accepted
    if ((route.requiresTOC && !session.tandc) || transferPasswordNeeded) {
      route.type = 'transfer';
    }

    // If the current route is a page, we'll display it accordingly.
    if (route.isPanelOpen) {
      panelComponent = (
        <Panel
          key="panel"
          locale={session.language}
          {...componentStates[route.type]}
        />
      );
    }

    // Mobile specific components
    if (Browser.isMobile) {
      navComponent = (
        <Sticky>
          <MobileNavigation
            menuItems={NavigationConfig.getItems()}
            route={route}
          />
        </Sticky>
      );
      footerComponent = <Footer />;
    } else {
      wallpaperComponent = this.renderWallpaperComponent();
      iconComponent = (
        <Logo
          route={route}
          progressPercentage={transferUpload.progress.percentage}
        />
      );
      navComponent = <Navigation systemNotification={noticeOptions.text} />;
      transferComponent = (
        <Transfer
          ref={(node) => (this.transferNode = node)}
          route={route}
          {...transferProps}
        />
      );
      dropzone = <Dropzone />;
    }

    const hasConsentFeature = isConsentActivated({ user, session, consent });

    // Show the cookies wall if cookie preference is not set
    if (hasConsentFeature) {
      if (Browser.isMobile) {
        if (!session.privacy.allowTracking && !session.privacy.notified) {
          cookieBanner = <CookieBanner tandcAccepted={session.tandc} />;
        } else {
          cookieBanner = null;
        }
      } else {
        welcome = (
          <Welcome isConsentActivated={hasConsentFeature} step={STEP_COOKIES} />
        );
        wallpaperComponent = null;
      }
    }

    // Show the terms and conditions wall if tandc are not accepted
    if (!session.tandc && !Browser.isMobile) {
      welcome = (
        <Welcome
          isConsentActivated={hasConsentFeature}
          step={STEP_TERMS_AND_CONDITIONS}
        />
      );
      cookieBanner = null;
      wallpaperComponent = null;
    }

    return (
      <TransitionGroup className={className} appear={true}>
        {noticeBar}
        {panelComponent}
        {welcome}
        {transferComponent}
        {dropzone}
        {wallpaperComponent}
        {navComponent}
        {iconComponent}
        {footerComponent}
        {cookieBanner}
        {this.state.showSplashpage && (
          <Splashpage
            onClick={this.closeSplashpage}
            onEscape={this.closeSplashpage}
            onFreeClick={this.closeSplashpage}
            onPlusClick={this.navigateToSignIn}
          />
        )}
      </TransitionGroup>
    );
  }
}

Layout.propTypes = {
  route: PropTypes.object,
};

export default Layout;
